/***
 * File name: pbnefMixin.js
 *
 * Program Description:
 * This file contains all the reusable data and functions for all PBNEF related
 * components.
 *
 * Includes:
 * - Mapping of PBNEF fields from the vuex store
 * - Module data tab name
 * - Computed properties relevant to a number of components
 * - Functions that fetch inital data needed for the fields within the PBNED Forms
 * - Registration of the PBNEF vuex module
 * - Fetch of the parent DBN collection
 * - Validations for the action button and forms restricting unauthorized users
 * - File upload and download functionalities
 * - Change of PBNEF assignee after status change
 *
 */
import { dataURLToFile } from '@/utils/helpers'
import { mapFields } from 'vuex-map-fields'
import crud from '@/utils/rest/crud'
import pbnef from '@/store/pbnef'

const pbnefMixin = {
  data: () => ({
    dbnDataSubjects: [],
    dbnSpecifyIfGroup: '',
    dbnSpecifyIfOthers: '',
    extensionDays: 0,
    objToFlat: [
      'howBreachOccurred',
      'chronology',
      'numberOfDataSubjects',
      'descriptionOrNature',
      'likelyConsequences',
      'dataProtectionOfficer',
      'actionsToMitigateHarm',
      'actionsToInformDataSubjects',
      'measuresToPreventRecurrence'
    ],
    natureToFlat: [
      'sensitivePersonalInformation',
      'identityFraud'
    ],
    howBreachOccurred: {
      title: ' 1.a How breach occurred + DPS vulnerability',
      info: 'Description of how the breach occurred and the vulnerability of the data processing system that allowed the breach.',
      details: '',
      provided: ''
    },
    chronology: {
      title: ' 1.b Chronology',
      info: 'Chronology of events.',
      details: ''
    },
    numberOfDataSubjects: {
      title: ' 1.c Number of DS / Records',
      info: 'Approximate number of data subjects or records involved.',
      details: ''
    },
    descriptionOrNature: {
      title: ' 1.d Description / Nature',
      info: 'An availability breach resulting from loss, accidental or unlawful destruction of personal data; Integrity breach resulting from alteration of personal data; and/or a confidentiality breach resulting from the unauthorized disclosure of or access to personal data.',
      details: ''
    },
    likelyConsequences: {
      title: ' 1.e Likely Consequences',
      info: 'Provide how the incident will affect both the Personal Information Controller and its data subjects.',
      details: ''
    },
    dataProtectionOfficer: {
      title: ' 1.f DPO',
      info: 'Name of the data protection officer or any other accountable person.',
      details: ''
    },
    sensitivePersonalInformation: {
      title: 'Sensitive Personal Information',
      info: `1. About an individual’s race, ethnic origin, marital status, age, color, and religious, philosophical or political affiliations; 
      2. About an individual’s health, education, genetic or sexual life of a person, or to any proceeding for any offense committed or alleged to have been committed by such person, the disposal of such proceedings, or the sentence of any court in such proceedings;
      3. Issued by government agencies peculiar to an individual which includes, but not limited to, social security numbers, previous or current health records, licenses or its denials, suspension or revocation, and tax returns, and
      4. Specifically established by an executive order or an act of Congress to be kept classified.
                `,
      details: ''
    },
    identityFraud: {
      title: 'Information that may enable identity fraud',
      info: 'Data about the financial or economic situation of the data subject; usernames, passwords and other login data; biometric data; copies of identification documents, licenses or unique identifiers like Philhealth, SSS, GSIS, TIN number; or other similar information, which may be made the basis of decisions concerning the data subject, including the grant of rights or benefits.',
      details: ''
    },
    measuresToAddress: {
      title: 'Measures to address the breach',
      info: 'Specific measures taken to address the incident including the results of the investigation conducted.',
      details: ''
    },
    measuresToSecure: {
      title: 'Measures to secure/recover personal data?',
      info: 'Actual measures taken to secure or recover personal data.',
      details: ''
    },
    actionsToMitigateHarm: {
      title: ' 3.c Actions to mitigate harm?',
      info: 'Actual measures taken to mitigate harm.',
      details: ''
    },
    actionsToInformDataSubjects: {
      title: ' 3.d Actions to inform data subjects?',
      info: 'The actual manner of notification including any assistance extended to data subjects.',
      details: ''
    },
    measuresToPreventRecurrence: {
      title: ' 3.e Measures to prevent recurrence of incidence',
      info: 'Actual or proposed orientation materials addressing the vulnerability identified.',
      details: ''
    },
    measuresToFlat: ['measuresToAddress', 'measuresToSecure'],
    formItem: {}
  }),
  computed: {
    ...mapFields('pbnef', {
      dbnNumber: 'dbnNumber',
      docketNumber: 'docketNumber',
      nameOfPic: 'nameOfPic',
      $meta: '$meta',
      dbnStatus: 'dbnStatus',
      typeOfRequests: 'typeOfRequests',
      users: 'users',
      status: 'status',
      isAssignee: 'isAssignee',
      statusObject: 'statusObject',
      endorsedTo: 'endorsedTo',
      approveReject: 'approveReject',
      typeOfRequestOther: 'typeOfRequestOther',
      isStageValid: 'isStageValid',
      pdbnfRequest: 'pdbnfRequest',
      _status: '_status',
      justification: 'justification'
    }),
    dataTab () {
      return this.$route.params.dataTab
    },
    dbn () {
      return this.$route.params.dbn
    },
    id () {
      return this.$route.params.dbn
    },
    operation () {
      return this.$route.params.operation
    },
    userRole () {
      return this.$s && this.$s.userRole
    },
    isEvaluator () {
      return this.userRole && this.userRole.includes('evaluator')
    },
    isReviewer () {
      return this.userRole && this.userRole.includes('reviewer')
    },
    isAdjudicator () {
      return this.userRole && this.userRole.includes('adjudicationRecipient')
    },
    isPic () {
      return this.userRole && this.userRole.includes('pic')
    },
    isPdbnfValid () {
      return this._status === 'active'
    },
    forInitialization () {
      return this.status === '' || !this.status ||
        (this.statusObject && this.statusObject.value === 'For Evaluation - PBNEF') ||
          (this.statusObject && this.statusObject.value === 'Drafted')
    },
    forFirstReview () {
      return this.statusObject && this.statusObject.value === 'Initial Review - PBNEF'
    },
    forFinalReview () {
      return this.statusObject && this.statusObject.value === 'Final Review - PBNEF'
    },
    forApproval () {
      return this.statusObject && this.statusObject.value === 'For Approval - PBNEF'
    },
    withEndorsement () {
      return this.endorsedTo && this.endorsedTo !== ''
    },
    notYetApproved () {
      return this.approveReject === '' || !this.approveReject
    },
    hasApproveReject () {
      const hasApproveReject = [...this.statusHistory].filter(item => item.status === 'Initial Order')
      return hasApproveReject.length > 0
    }
  },
  async created () {
    this.$meta.loading = true
    await this.getDbnData()
    await this.getDbnStatus()
    await this.setStatusObj()
    await this.stageValidation()
    await this.setAssignee()
    await this.getUsers()
    await this.initNatureOfPersonalData()
    if (this.operation === 'create') {
      this.dataSubjects = this.dbnDataSubjects
      this.specifyIfOthers = this.dbnSpecifyIfOthers
      this.specifyIfGroup = this.dbnSpecifyIfGroup
      this.numberOfDataSubjectsAffected = this.dbnNumberOfDataSubjectsAffected
      this.atLeastOneHundredIndividuals = this.dbnAtLeastOneHundredIndividuals
    }
    this.$meta.loading = false
  },
  beforeCreate () {
    if (!this.$store.hasModule('pbnef')) {
      this.$store.registerModule('pbnef', pbnef)
    }
  },
  methods: {
    setStatusObj () {
      this.statusObject = this.dbnStatus.find(x => x._id === this.status)
    },
    api (action) {
      return crud(this.$meta.routes[`${action}`])
    },
    async getRequests (typeOfRequest) {
      const advancedQuery = { docketNumber: this.dbnNumber }
      const dataview = 'table'
      const list = await crud('pbnef').list({ advancedQuery, dataview })

      this.submittedPBNEF = [...list.entries].filter(x => x.isSubmitted)
      const all = list.entries.reduce((acc, curr) => {
        acc.push(...curr.requestFor)
        return acc
      }, [])

      const unique = [...new Set(all)]
      return typeOfRequest.filter(x => {
        if (this.operation !== 'create' && this.requestFor && this.requestFor.includes(x)) return x
        return !unique.includes(x)
      })
    },
    async getDbnData () {
      const data = await this.api('dbn').read(this.dbn)
      const {
        dbnNumber,
        pic,
        typeOfRequest,
        assignedTo,
        statusHistory,
        typeOfRequestOther,
        notificationType,
        dataSubjects,
        extensionDays,
        specifyDataSubjectsOthers,
        specifyDataSubjectsVulnerable,
        numberOfDataSubjectsAffected,
        atLeastOneHundredIndividuals,
        _status,
        requestExtension,
        justificationAlternative,
        justificationExemption,
        justificationPostponement
      } = data.entry

      if (dbnNumber) {
        this.dbnNumber = dbnNumber
        this.docketNumber = dbnNumber
        this.pdbnfRequest = typeOfRequest
        this.typeOfRequests = await this.getRequests(typeOfRequest)
        this.nameOfPic = pic.organization
        this.assignedTo = assignedTo
        this.statusHistory = statusHistory
        this.typeOfRequestOther = typeOfRequestOther
        this.dbnDataSubjects = dataSubjects
        this.dbnSpecifyIfGroup = specifyDataSubjectsVulnerable
        this.dbnSpecifyIfOthers = specifyDataSubjectsOthers
        this.dbnNumberOfDataSubjectsAffected = numberOfDataSubjectsAffected
        this.dbnAtLeastOneHundredIndividuals = atLeastOneHundredIndividuals
        this.notificationType = notificationType
        this.extensionDays = extensionDays
        this._status = _status
        this.justification = {
          alternative: justificationAlternative,
          exemption: justificationExemption,
          postponement: justificationPostponement,
          extension: requestExtension
        }
        const keys = Object.keys(data.entry)
        await keys.forEach((field) => {
          if ((this.objToFlat.includes(field) || this.measuresToFlat.includes(field) || this.natureToFlat.includes(field)) && data.entry[`${field}`]) {
            this.flattenObj(field, data.entry[`${field}`])
          }
        })
      }
    },

    async getDbnStatus () {
      const response = await crud('dbnStatus').list({
        count: 10000
      })
      this.dbnStatus = response.entries
    },
    async getUsers () {
      let response = null
      let counter = 1
      do {
        response = await crud('admin/users').list({
          count: 100000 * counter
        })
        counter++
      } while (response && response.ok && (response.entries.length < response.total))

      this.users = response.entries
    },
    getStatusObj (status) {
      return this.dbnStatus.find(x => x.value === status)
    },
    flattenObj (field, obj) {
      if (obj.details) {
        this[`${field}`].details = obj.details
      }
      if (obj.sufficientlyProvided) {
        this[`${field}`].provided = obj.sufficientlyProvided
      }
    },
    async stageValidation () {
      let submittedStatus = this.submittedPBNEF.reduce((acc, curr) => {
        acc.push(...curr.requestFor)
        return acc
      }, [])
      submittedStatus = [...new Set(submittedStatus)]

      if (submittedStatus.length !== this.pdbnfRequest.length || !this.statusObject) return false

      const initObj = this.getStatusObj('Initial Review - PBNEF')
      const finalObj = this.getStatusObj('Final Review - PBNEF')
      const approvalObj = this.getStatusObj('For Approval - PBNEF')
      const forEvalObj = this.getStatusObj('For Evaluation')
      const initOrderObj = this.getStatusObj('Initial Order')

      // user can only update/approve the assigned pbenf to her
      // if all other pbnef's from the same pdbnf is with the same status
      // or one status ahead
      const submitted = [...this.submittedPBNEF].filter(x => {
        if (x.approveReject !== 'Approved' && x.approveReject !== 'Rejected') return x
      })
      const submittedPBNEF = this.hasApproveReject ? submitted : this.submittedPBNEF

      switch (this.statusObject.value) {
        case 'For Evaluation - PBNEF':
          this.isStageValid = submittedPBNEF.every(pbnef => {
            return (pbnef.status === this.statusObject._id) || (pbnef.status === initObj._id)
          })
          break
        case 'Initial Review - PBNEF':
          this.isStageValid = submittedPBNEF.every(pbnef => {
            return (pbnef.status === this.statusObject._id) || (pbnef.status === finalObj._id)
          })
          break
        case 'For Approval - PBNEF':
        case 'Final Review - PBNEF':
        case 'For Evaluation':
        case 'Initial Order':
          this.isStageValid = submittedPBNEF.every(pbnef => {
            return (pbnef.status === (finalObj && finalObj._id)) || // after initial review
              (pbnef.status === (approvalObj && approvalObj._id)) || // if endorsed
              (pbnef.status === (forEvalObj && forEvalObj._id)) || // two possible status after approval
              (pbnef.status === (initOrderObj && initOrderObj._id))
          })
          break
      }
    },
    async setAssignee () {
      if (!this.assignedTo || !this.assignedTo.evaluator.fullName) this.isAssignee = false

      const { evaluator, firstPass, final } = this.assignedTo
      const userID = this.$s.user

      if (!this.statusObject || this.status === '' || !this.status) {
        this.isAssignee = evaluator._id === userID
      }

      if (this.statusObject) {
        switch (this.statusObject.value) {
          case 'For Evaluation - PBNEF':
          case 'Drafted':
            this.isAssignee = evaluator._id === userID
            break
          case 'Initial Review - PBNEF':
            this.isAssignee = firstPass._id === userID
            break
          case 'Final Review - PBNEF':
            this.isAssignee = final._id === userID
            break
          case 'For Approval - PBNEF':
            this.isAssignee = this.endorsedTo === userID
            break
        }
      }
    },
    async fileUploadForm (formParams) {
      const form = new FormData()
      Object.keys(formParams.fields).forEach(key => {
        if (Array.isArray(formParams.fields[`${key}`])) {
          formParams.fields[`${key}`].forEach(el => {
            form.append(key, el)
          })
        } else form.append(key, formParams.fields[`${key}`])
      })
      return form
    },
    async fileUploader (payloadJson, type = 'pbnef') {
      const dataForm = await this.fileUploadForm(this.formParams)

      const resp = await Promise.all(Object.keys(this.formParams.fields).map(async (key) => {
        if (Array.isArray(dataForm.getAll(key))) {
          if (dataForm.getAll(key).every((file) => file.size && file.size > 0)) {
            return await crud(`attachment/${type}`).createForm(
              dataForm,
              this.formParams.query,
              this.$route.params.id || (payloadJson.entry && payloadJson.entry._id) || (payloadJson.entry && payloadJson.entry._id),
              key
            )
          }
        } else {
          if (dataForm.get(key).size > 0) {
            return await crud(`attachment/${type}`).createForm(
              dataForm,
              this.formParams.query,
              this.$params.id || payloadJson.entry._id,
              key
            )
          }
        }
      }))
      if (resp && resp[0] === undefined) return

      const successUpload = resp.every(x => x && x.ok)
      let errorMsgs = []
      if (!successUpload) {
        errorMsgs = await Promise.all(resp.map(async (file) => {
          const data = await file.json()
          return data.error
        }))
      }

      return {
        ok: successUpload,
        error: errorMsgs
      }
    },
    async download (url, file) {
      if (Array.isArray(file)) {
        await Promise.all(file.map((item) => {
          this.fileDownload(url, item)
        }))
      } else this.fileDownload(url, file)
    },
    async fileDownload (url, file) {
      const route = `${url}/${file.fieldname || 'fileUpload'}/${file.filename || file.name}`
      const resp = await crud(route).get()
      if (resp.ok) {
        const data = await resp.json()
        const downloadLink = document.createElement('a')
        downloadLink.href = data.file
        downloadLink.download = file.originalname || file.filename || file.name
        downloadLink.click()
      } else {
        this.localDownload(file)
      }
    },
    async localDownload (file) {
      if (Array.isArray(file)) {
        file.forEach((item) => this.downloadFile(item, item.name))
      } else this.downloadFile(file, file.name)
    },
    downloadFile (blob, name) {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.style.display = 'none'
      link.href = url
      link.download = name
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(url)
      link.parentNode.removeChild(link)
    },
    async getImage (id, file) {
      if (!file) return ''
      const getFile = []
      if (Array.isArray(file)) {
        for (let i = 0; i < file.length; i++) {
          this.fetchImage(getFile, id, file[`${i}`])
        }
      } else this.fetchImage(getFile, id, file)
      return getFile
    },
    async fetchImage (getFile, id, file) {
      const { filename, mimetype } = file
      const data = await crud('attachment/pbnef').fileUpload(id, filename, {})
      getFile.push(await dataURLToFile(data.file, filename, mimetype))
    },
    async initNatureOfPersonalData () {
      if (this.operation !== 'create') return
      if (!this.notificationType.length || !this.natureOfPersonalDataInvolved) return
      const notifType = this.notificationType.map(notif => notif.value)

      for (const notif of notifType) {
        const data = await crud('notificationType').read(notif)
        const caseOne = data.entry.value === 'Involves SPI or data that may enable identity fraud'

        if (!caseOne) continue

        if (caseOne && !this.natureOfPersonalDataInvolved.checklist.includes('Sensitive Personal Information Involved')) {
          this.natureOfPersonalDataInvolved.checklist.push('Sensitive Personal Information Involved')
          this.natureOfPersonalDataInvolved.checklist.push('Information that could enable identity fraud')
        }
      }
    }
  }
}

export default pbnefMixin
